import { useEffect, useState } from "react";
import { Archive, FileSpreadsheet, Star, Mail, Loader2 } from "lucide-react";
import { requestExport } from "@/components/portal/CandidatesTable";
import { ShareFavorites } from "@/components/portal/ShareFavorites";
import { api } from "@/lib/api";
import { fmt, fullName } from "@/lib/demo/utils";
import { shortTit } from "@/lib/demo/seed";
import { ORIGIN_SHORT } from "@/lib/i18n/dictInteracciones";
import { useI18n } from "@/lib/i18n/i18n";

const Kpi = ({ label, value, testId, dark }) => {
  const { t } = useI18n();
  return (
    <div className={`rounded-[24px] border p-6 ${dark ? "border-slate-950 bg-slate-950 text-white" : "border-slate-200 bg-white"}`}>
      <p className={`text-[11px] font-bold uppercase tracking-[0.2em] ${dark ? "text-white/50" : "text-slate-400"}`}>{t(label)}</p>
      <p className="mt-3 font-display text-4xl font-bold tracking-tight" data-testid={testId}>{fmt(value)}</p>
    </div>
  );
};

const Bars = ({ title, items, testId }) => {
  const { t } = useI18n();
  const max = Math.max(1, ...items.map((x) => x.value));
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-6" data-testid={testId}>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{t(title)}</p>
      <ul className="mt-4 space-y-3">
        {items.map((x) => (
          <li key={x.name} className="text-sm">
            <div className="flex justify-between gap-3"><span className="truncate font-semibold">{t(x.name)}</span><span className="font-mono font-bold">{fmt(x.value)}</span></div>
            <div className="mt-1 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-upv transition-[width] duration-700" style={{ width: `${(x.value / max) * 100}%` }} /></div>
          </li>
        ))}
        {items.length === 0 && <li className="text-sm text-slate-400">—</li>}
      </ul>
    </div>
  );
};

const Downloads = ({ favorites }) => {
  const { t } = useI18n();
  const btn = "flex h-12 items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold transition-colors";
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
      <button onClick={() => requestExport("zip", {}, t)} className={`${btn} bg-upv text-white hover:bg-upv-dark`} data-testid="report-download-zip"><Archive className="h-4 w-4" /> {t("Descargar todos los CV (ZIP)")}</button>
      <button onClick={() => requestExport("zip", { favorite: true }, t)} disabled={!favorites} className={`${btn} border-2 border-slate-900 hover:bg-slate-900 hover:text-white disabled:opacity-40`} data-testid="report-download-zip-favorites"><Star className="h-4 w-4" /> {t("ZIP solo favoritos")}</button>
      <button onClick={() => requestExport("xlsx", {}, t)} className={`${btn} bg-emerald-700 text-white hover:bg-emerald-800`} data-testid="report-download-excel"><FileSpreadsheet className="h-4 w-4" /> {t("Excel con favoritos y notas")}</button>
    </div>
  );
};

export const PortalReport = ({ onOpen, refreshKey }) => {
  const { t, lang } = useI18n();
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/company/report").then((x) => setR(x.data)).catch(() => setR(false)); }, [refreshKey]);
  if (r === null) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  if (!r) return null;
  const origins = Object.entries(r.byOrigin).map(([k, v]) => ({ name: ORIGIN_SHORT[k] || k, value: v }));
  return (
    <div className="space-y-6" data-testid="portal-report">
      <div className="flex flex-col gap-4 rounded-[28px] bg-white p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Resumen post-evento")}</p>
          <p className="mt-2 max-w-xl text-sm text-slate-600">{t("Todos los currículums recibidos durante y después del Foro, con su origen y las valoraciones de tu equipo.")}</p>
          <p className="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-500" data-testid="report-last-sent"><Mail className="h-3.5 w-3.5" /> {r.lastSentAt ? t("Enviado por email el {d}", { d: new Date(r.lastSentAt).toLocaleString(lang, { dateStyle: "medium", timeStyle: "short" }) }) : t("El resumen aún no se ha enviado por email.")}</p>
        </div>
        <Downloads favorites={r.favorites} />
      </div>
      <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-6">
        <Kpi label="Candidatos" value={r.total} testId="report-kpi-total" dark />
        <Kpi label="Día 1" value={r.day1} testId="report-kpi-day1" />
        <Kpi label="Día 2" value={r.day2} testId="report-kpi-day2" />
        <Kpi label="Online / fuera de fechas" value={r.total - r.day1 - r.day2} testId="report-kpi-outside" />
        <Kpi label="Favoritos" value={r.favorites} testId="report-kpi-favorites" />
        <Kpi label="Notas" value={r.notes} testId="report-kpi-notes" />
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <Bars title="Origen de los currículums" items={origins} testId="report-origins" />
        <Bars title="Titulaciones principales" items={r.titulaciones.map((x) => ({ ...x, name: shortTit(x.name) }))} testId="report-titulaciones" />
        <Bars title="Centros principales" items={r.centros} testId="report-centros" />
      </div>
      <div className="rounded-[24px] border border-slate-200 bg-white p-6" data-testid="report-favorites">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-400"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" /> {t("Favoritos del equipo")}</p>
        <ul className="mt-4 divide-y divide-slate-100">
          {r.favoriteRows.map((row) => (
            <li key={row.interaction.id}>
              <button onClick={() => onOpen(row)} className="flex w-full items-center justify-between gap-3 py-3 text-left text-sm hover:text-upv" data-testid={`report-favorite-${row.participant.id}`}>
                <span className="font-semibold">{fullName(row.participant)}</span>
                <span className="truncate text-xs text-slate-500">{t(shortTit(row.participant.titulacion))} · {t(ORIGIN_SHORT[row.interaction.origin] || "")}</span>
              </button>
            </li>
          ))}
          {r.favoriteRows.length === 0 && <li className="py-3 text-sm text-slate-400">{t("Marca candidatos con la estrella para verlos aquí.")}</li>}
        </ul>
      </div>
      <ShareFavorites favorites={r.favorites} />
    </div>
  );
};
